import React from 'react';
import { useAuth } from '../context/useAuth';

/**
 * Global Watermark component
 * Renders an overlay based on the branding.watermark_config
 * fetched from tenant_branding (Branding → Advanced tab).
 *
 * Falls back to default values only if no tenant configuration is set.
 */
const Watermark = () => {
  const { branding } = useAuth();
  
  if (!branding?.watermark_config?.watermark_enabled) {
    return null;
  }

  const {
    watermark_type = 'text',
    watermark_text = '',
    watermark_opacity = 30,
    watermark_position = 'bottom-right'
  } = branding.watermark_config;

  const opacity = watermark_opacity / 100;

  // ── Position styles (matches all placements from BrandingAdvancedTab) ──
  const positionStyles = {
    'top-left':      { top: '2rem', left: '2rem' },
    'top-center':    { top: '2rem', left: '50%', transform: 'translateX(-50%)' },
    'top-right':     { top: '2rem', right: '2rem' },
    'center-left':   { top: '50%', left: '2rem', transform: 'translateY(-50%)' },
    'center':        { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' },
    'center-right':  { top: '50%', right: '2rem', transform: 'translateY(-50%)' },
    'bottom-left':   { bottom: '2rem', left: '2rem' },
    'bottom-center': { bottom: '2rem', left: '50%', transform: 'translateX(-50%)' },
    'bottom-right':  { bottom: '2rem', right: '2rem' },
  };

  const isDiagonal = watermark_position.startsWith('diagonal');
  const isTile     = watermark_position === 'tile';

  // ── Base style (shared across all modes) ──
  const baseStyle = {
    position: 'absolute',
    pointerEvents: 'none',
    zIndex: 50, // Lowered from 9999 to stay within content area
    opacity,
    userSelect: 'none',
  };

  // ── IMAGE watermark ──
  if (watermark_type === 'image') {
    const logoUrl = branding.logo_light_url;
    if (!logoUrl) return null;

    if (isTile) {
      return (
        <div
          style={{ 
            ...baseStyle, 
            inset: 0, 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
            gridAutoRows: '150px',
            gap: '2rem', 
            padding: '2rem', 
            overflow: 'hidden' 
          }}
          id="tenant-watermark"
        >
          {Array(48).fill(0).map((_, i) => (
            <div key={i} className="flex items-center justify-center">
              <img src={logoUrl} alt="" style={{ maxHeight: '60px', maxWidth: '120px', filter: 'grayscale(100%)', transform: 'rotate(-12deg)' }} />
            </div>
          ))}
        </div>
      );
    }

    if (isDiagonal) {
      return (
        <div
          style={{ ...baseStyle, inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          id="tenant-watermark"
        >
          <img
            src={logoUrl}
            alt=""
            style={{
              maxHeight: '120px', maxWidth: '240px', filter: 'grayscale(100%)',
              transform: `rotate(${watermark_position === 'diagonal-rt-lb' ? '-35deg' : '35deg'})`
            }}
          />
        </div>
      );
    }

    // Fixed position
    return (
      <div style={{ ...baseStyle, ...(positionStyles[watermark_position] || positionStyles['bottom-right']) }} id="tenant-watermark">
        <img src={logoUrl} alt="" style={{ maxHeight: '100px', maxWidth: '200px', filter: 'grayscale(100%)' }} />
      </div>
    );
  }

  // ── TEXT watermark ──
  const displayText = watermark_text || branding.name || '';
  if (!displayText && watermark_type === 'text') return null;

  const textStyle = {
    fontWeight: 'bold',
    color: 'rgba(150, 150, 150, 0.5)',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
  };

  // Tile mode — repeat text across the viewport
  if (isTile) {
    return (
      <div
        style={{ 
          ...baseStyle, 
          inset: 0, 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
          gridAutoRows: '150px',
          gap: '2rem', 
          padding: '2rem', 
          overflow: 'hidden' 
        }}
        id="tenant-watermark"
      >
        {Array(100).fill(0).map((_, i) => (
          <div key={i} className="flex items-center justify-center">
            <span style={{ ...textStyle, fontSize: '1.2rem', transform: 'rotate(-15deg)', whiteSpace: 'nowrap' }}>
              {displayText}
            </span>
          </div>
        ))}
      </div>
    );
  }

  // Diagonal mode
  if (isDiagonal) {
    return (
      <div
        style={{ ...baseStyle, inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}
        id="tenant-watermark"
      >
        <span style={{
          ...textStyle,
          fontSize: '5vw',
          whiteSpace: 'nowrap',
          transform: `rotate(${watermark_position === 'diagonal-rt-lb' ? '-35deg' : '35deg'})`
        }}>
          {displayText}
        </span>
      </div>
    );
  }

  // Fixed position mode
  return (
    <div
      style={{
        ...baseStyle,
        ...(positionStyles[watermark_position] || positionStyles['bottom-right']),
        ...textStyle,
        fontSize: '1.5rem',
      }}
      id="tenant-watermark"
    >
      {displayText}
    </div>
  );
};

export default Watermark;
